import { useEffect, useState } from "react";
import { AlertTriangle, GitBranch, Loader2, PlayCircle } from "lucide-react";
import {
  reviewCrmEventActionRecommendations,
  type CrmEventActionRecommendationReviewResult,
} from "@/hooks/use-crm-event-action-score";
import { useCrmEventActionAutomationPolicy } from "@/hooks/use-crm-event-action-automation-policy";

interface EventActionReviewBridgeProps {
  onQueued?: () => void;
  signalFamily?: string;
  sourceEventType?: string;
  onSignalFamilySelect?: (signalFamily: string) => void;
  onSourceEventTypeSelect?: (sourceEventType: string, signalFamily: string) => void;
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">{label}</p>
      <p className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">{value}</p>
    </div>
  );
}

function prettyEventType(value: string): string {
  return value
    .replace(/^crm\./, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function eventTypeFamily(value: string): string {
  if (value.includes("webinar")) return "webinar";
  if (value.includes("booking") || value.includes("form") || value.includes("meeting")) return "conversion";
  if (value.includes("payment") || value.includes("deal.won")) return "revenue";
  if (value.includes("email") || value.includes("page") || value.includes("activity") || value.includes("call") || value.includes("message")) return "engagement";
  if (value.includes("deal")) return "pipeline";
  if (value.includes("task") || value.includes("followup")) return "operator_workflow";
  if (value.includes("person") || value.includes("company")) return "profile";
  return "other";
}

function prettyFamily(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function groupEventTypes(eventTypes: string[]): Array<{ family: string; eventTypes: string[] }> {
  const groups = new Map<string, string[]>();
  for (const eventType of eventTypes) {
    const family = eventTypeFamily(eventType);
    groups.set(family, [...(groups.get(family) ?? []), eventType]);
  }

  const familyOrder = ["profile", "engagement", "conversion", "webinar", "revenue", "pipeline", "operator_workflow", "other"];
  return Array.from(groups.entries())
    .map(([family, values]) => ({ family, eventTypes: values.sort() }))
    .sort((a, b) => familyOrder.indexOf(a.family) - familyOrder.indexOf(b.family));
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function toggleEventType(current: string[], eventType: string): string[] {
  return current.includes(eventType)
    ? current.filter((item) => item !== eventType)
    : [...current, eventType];
}

function toggleSignalFamily(current: string[], signalFamily: string): string[] {
  return current.includes(signalFamily)
    ? current.filter((item) => item !== signalFamily)
    : [...current, signalFamily];
}

export function EventActionReviewBridge({
  onQueued,
  signalFamily,
  sourceEventType,
  onSignalFamilySelect,
  onSourceEventTypeSelect,
}: EventActionReviewBridgeProps) {
  const [result, setResult] = useState<CrmEventActionRecommendationReviewResult | null>(null);
  const [isRunning, setIsRunning] = useState<"preview" | "queue" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    data: automationPolicy,
    isLoading: isPolicyLoading,
    error: policyError,
    updatePolicy,
  } = useCrmEventActionAutomationPolicy();
  const [isSavingPolicy, setIsSavingPolicy] = useState(false);
  const [sourceSurfaceDraft, setSourceSurfaceDraft] = useState("");

  useEffect(() => {
    if (automationPolicy) {
      setSourceSurfaceDraft(automationPolicy.sourceSurface);
    }
  }, [automationPolicy]);

  const runBridge = async (dryRun: boolean) => {
    setIsRunning(dryRun ? "preview" : "queue");
    setError(null);
    const activePolicy = automationPolicy ?? {
      decision: "accepted" as const,
      days: 7,
      limit: 50,
      minConfidence: 0.55,
      includeManualReview: true,
      includeBlocked: false,
      eventTypes: [],
      signalFamilies: [],
      sourceSurface: "crm.today.action_loop",
    };

    try {
      const nextResult = await reviewCrmEventActionRecommendations({
        dryRun,
        decision: activePolicy.decision,
        days: activePolicy.days,
        limit: activePolicy.limit,
        minConfidence: activePolicy.minConfidence,
        includeManualReview: activePolicy.includeManualReview,
        includeBlocked: activePolicy.includeBlocked,
        eventTypes: activePolicy.eventTypes,
        signalFamilies: activePolicy.signalFamilies,
        sourceSurface: activePolicy.sourceSurface,
      });
      setResult(nextResult);
      if (!dryRun && nextResult.summary.recorded > 0) {
        onQueued?.();
      }
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to review CRM event actions");
    } finally {
      setIsRunning(null);
    }
  };

  const patchPolicy = async (patch: Partial<Omit<NonNullable<typeof automationPolicy>, "source" | "availableEventTypes" | "availableSignalFamilies">>) => {
    if (!automationPolicy) {
      return;
    }

    setIsSavingPolicy(true);
    setError(null);

    try {
      await updatePolicy({
        enabled: automationPolicy.enabled,
        decision: automationPolicy.decision,
        minConfidence: automationPolicy.minConfidence,
        days: automationPolicy.days,
        limit: automationPolicy.limit,
        includeManualReview: automationPolicy.includeManualReview,
        includeBlocked: automationPolicy.includeBlocked,
        eventTypes: automationPolicy.eventTypes,
        signalFamilies: automationPolicy.signalFamilies,
        sourceSurface: automationPolicy.sourceSurface,
        ...patch,
      });
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to update automation policy");
    } finally {
      setIsSavingPolicy(false);
    }
  };

  const applyCurrentScopeToPolicy = async () => {
    if (!automationPolicy || (!signalFamily && !sourceEventType)) {
      return;
    }

    await patchPolicy({
      signalFamilies: signalFamily ? [signalFamily] : sourceEventType ? [eventTypeFamily(sourceEventType)] : [],
      eventTypes: sourceEventType ? [sourceEventType] : [],
    });
  };

  const summary = result?.summary;
  const latestItems = (result?.dryRun ? result.preview : result?.recorded ?? []).slice(0, 3);
  const availableEventTypeGroups = automationPolicy ? groupEventTypes(automationPolicy.availableEventTypes) : [];
  const availableSignalFamilies = automationPolicy
    ? automationPolicy.availableSignalFamilies.length > 0
      ? automationPolicy.availableSignalFamilies
      : availableEventTypeGroups.map((group) => group.family)
    : [];
  const selectedFamilies = automationPolicy
    ? automationPolicy.signalFamilies.length > 0
      ? automationPolicy.signalFamilies
      : Array.from(new Set(automationPolicy.eventTypes.map(eventTypeFamily))).sort()
    : [];
  const policyScopeParts = automationPolicy
    ? [
      automationPolicy.signalFamilies.length > 0 ? `${automationPolicy.signalFamilies.length} families` : "all families",
      automationPolicy.eventTypes.length > 0 ? `${automationPolicy.eventTypes.length} source events` : "all source events",
    ]
    : [];
  const pageScopeParts = [
    signalFamily ? `${prettyFamily(signalFamily)} family` : null,
    sourceEventType ? prettyEventType(sourceEventType) : null,
  ].filter(Boolean);
  const pageScopeMatchesPolicy = automationPolicy ? (
    (!signalFamily || automationPolicy.signalFamilies.includes(signalFamily))
    && (!sourceEventType || automationPolicy.eventTypes.includes(sourceEventType))
  ) : false;

  return (
    <section className="rounded-2xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-primary)] text-white">
            <GitBranch className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--color-foreground)]">Event-driven action bridge</p>
            <p className="mt-1 max-w-2xl text-xs text-[var(--color-muted-foreground)]">
              Score recent CRM events and record accepted action recommendations for the existing staff-gated queue. This does not execute CRM mutations.
            </p>
            {pageScopeParts.length > 0 && (
              <p className="mt-2 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-1 text-xs text-[var(--color-primary)]">
                Page scope: {pageScopeParts.join(" · ")}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void runBridge(true)}
            disabled={isRunning !== null}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isRunning === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
            Preview
          </button>
          <button
            type="button"
            onClick={() => void runBridge(false)}
            disabled={isRunning !== null}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isRunning === "queue" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitBranch className="h-3.5 w-3.5" />}
            Queue recommendations
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-xs text-[var(--color-destructive)]">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      <div className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium text-[var(--color-foreground)]">Automation policy</span>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] ${
            automationPolicy?.enabled
              ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]"
              : "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
          }`}>
            {isPolicyLoading ? "Loading" : automationPolicy?.enabled ? "Enabled" : "Disabled"}
          </span>
        </div>
        {automationPolicy && (
          <>
            <p className="mt-1 text-[var(--color-muted-foreground)]">
              {automationPolicy.source} · {automationPolicy.days}d window · limit {automationPolicy.limit} · min {Math.round(automationPolicy.minConfidence * 100)}%
              {automationPolicy.includeManualReview ? " · includes manual review" : ""}
              {automationPolicy.signalFamilies.length > 0 ? ` · ${automationPolicy.signalFamilies.length} signal families` : ""}
              {automationPolicy.eventTypes.length > 0 ? ` · ${automationPolicy.eventTypes.length} event types` : " · all scorable CRM signals"}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-[10px] text-[var(--color-muted-foreground)]">
              <span>Policy scope: {policyScopeParts.join(" · ")}</span>
              {pageScopeParts.length > 0 && (
                <button
                  type="button"
                  disabled={isSavingPolicy || pageScopeMatchesPolicy}
                  onClick={() => void applyCurrentScopeToPolicy()}
                  className="rounded-full border border-[var(--color-border)] px-2 py-0.5 font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {pageScopeMatchesPolicy ? "Policy matches page scope" : "Apply page scope to policy"}
                </button>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(selectedFamilies.length > 0 ? selectedFamilies : availableEventTypeGroups.map((group) => group.family)).map((family) => (
                <button
                  key={family}
                  type="button"
                  onClick={() => onSignalFamilySelect?.(family)}
                  className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                >
                  {prettyFamily(family)}
                </button>
              ))}
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-4">
              <label className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-2 py-1.5 text-[var(--color-muted-foreground)]">
                <input
                  type="checkbox"
                  checked={automationPolicy.enabled}
                  disabled={isSavingPolicy}
                  onChange={(event) => void patchPolicy({ enabled: event.target.checked })}
                />
                Enabled
              </label>
              <label className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                Decision
                <select
                  value={automationPolicy.decision}
                  disabled={isSavingPolicy}
                  onChange={(event) => void patchPolicy({ decision: event.target.value === "dismissed" ? "dismissed" : "accepted" })}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs text-[var(--color-foreground)]"
                >
                  <option value="accepted">Accept recommendations</option>
                  <option value="dismissed">Dismiss recommendations</option>
                </select>
              </label>
              <label className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                Min confidence
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round(automationPolicy.minConfidence * 100)}
                  disabled={isSavingPolicy}
                  onChange={(event) => void patchPolicy({ minConfidence: Math.min(Math.max(Number(event.target.value) / 100, 0), 1) })}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs text-[var(--color-foreground)]"
                />
              </label>
              <label className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                Window days
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={automationPolicy.days}
                  disabled={isSavingPolicy}
                  onChange={(event) => void patchPolicy({ days: Math.min(Math.max(Number(event.target.value), 1), 365) })}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs text-[var(--color-foreground)]"
                />
              </label>
              <label className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                Limit
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={automationPolicy.limit}
                  disabled={isSavingPolicy}
                  onChange={(event) => void patchPolicy({ limit: Math.min(Math.max(Number(event.target.value), 1), 100) })}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs text-[var(--color-foreground)]"
                />
              </label>
              <label className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-2 py-1.5 text-[var(--color-muted-foreground)]">
                <input
                  type="checkbox"
                  checked={automationPolicy.includeManualReview}
                  disabled={isSavingPolicy}
                  onChange={(event) => void patchPolicy({ includeManualReview: event.target.checked })}
                />
                Include manual review
              </label>
              <label className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-2 py-1.5 text-[var(--color-muted-foreground)]">
                <input
                  type="checkbox"
                  checked={automationPolicy.includeBlocked}
                  disabled={isSavingPolicy}
                  onChange={(event) => void patchPolicy({ includeBlocked: event.target.checked })}
                />
                Include blocked
              </label>
            </div>
            <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-2">
              <div className="flex flex-wrap items-end gap-2">
                <label className="min-w-0 flex-1 text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  Source surface
                  <input
                    type="text"
                    value={sourceSurfaceDraft}
                    disabled={isSavingPolicy}
                    onChange={(event) => setSourceSurfaceDraft(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-foreground)]"
                  />
                </label>
                <button
                  type="button"
                  disabled={isSavingPolicy || sourceSurfaceDraft.trim() === "" || sourceSurfaceDraft.trim() === automationPolicy.sourceSurface}
                  onClick={() => void patchPolicy({ sourceSurface: sourceSurfaceDraft.trim() })}
                  className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Save source
                </button>
              </div>
            </div>
            <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  Signal family filter
                </p>
                <button
                  type="button"
                  disabled={isSavingPolicy || automationPolicy.signalFamilies.length === 0}
                  onClick={() => void patchPolicy({ signalFamilies: [] })}
                  className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Use all families
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {availableSignalFamilies.map((family) => {
                  const isSelected = automationPolicy.signalFamilies.includes(family);

                  return (
                    <button
                      key={family}
                      type="button"
                      disabled={isSavingPolicy}
                      onClick={() => void patchPolicy({ signalFamilies: toggleSignalFamily(automationPolicy.signalFamilies, family) })}
                      className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                        isSelected
                          ? "border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                          : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                      }`}
                    >
                      {prettyFamily(family)}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[10px] text-[var(--color-muted-foreground)]">
                Family filters persist as reusable policy. Event type filters below can further narrow the selected families.
              </p>
            </div>
            <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
                  Event type filter
                </p>
                <button
                  type="button"
                  disabled={isSavingPolicy || automationPolicy.eventTypes.length === 0}
                  onClick={() => void patchPolicy({ eventTypes: [] })}
                  className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Use all signals
                </button>
              </div>
              <div className="mt-2 max-h-48 space-y-2 overflow-y-auto pr-1">
                {availableEventTypeGroups.map((group) => (
                  <div key={group.family}>
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
                        {prettyFamily(group.family)}
                      </span>
                      <span className="text-[10px] text-[var(--color-muted-foreground)]">{group.eventTypes.length}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {group.eventTypes.map((eventType) => {
                        const isSelected = automationPolicy.eventTypes.includes(eventType);

                        return (
                          <button
                            key={eventType}
                            type="button"
                            disabled={isSavingPolicy}
                            onClick={() => void patchPolicy({ eventTypes: toggleEventType(automationPolicy.eventTypes, eventType) })}
                            className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                              isSelected
                                ? "border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                                : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                            }`}
                          >
                            {prettyEventType(eventType)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {policyError && (
          <p className="mt-1 text-[var(--color-warning)]">{policyError}</p>
        )}
      </div>

      {summary && (
        <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-6">
          <StatTile label="Events" value={summary.sourceEventsScanned} />
          <StatTile label="Recommendations" value={summary.recommendationsScored} />
          <StatTile label="Candidates" value={summary.candidates} />
          <StatTile label={result?.dryRun ? "Previewed" : "Queued"} value={result?.dryRun ? result.preview.length : summary.recorded} />
          <StatTile label="Duplicates" value={summary.skippedDuplicate} />
          <StatTile label="Blocked" value={summary.skippedBlocked} />
        </div>
      )}

      {latestItems.length > 0 && (
        <div className="mt-3 space-y-2">
          {latestItems.map((item) => (
            <div key={`${item.sourceEventId}-${item.recommendationId}`} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-[var(--color-foreground)]">{item.actionRef}</span>
                <div className="flex flex-wrap gap-1">
                  <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)]">
                    {item.priority} · {item.readinessStatus} · {Math.round(item.confidence * 100)}%
                  </span>
                  {item.queueEligibility && (
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${
                      item.queueEligibility === "executable"
                        ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]"
                        : item.queueEligibility === "manual_review"
                          ? "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                          : "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
                    }`}>
                      {prettyEventType(item.queueEligibility)}
                    </span>
                  )}
                  {item.adjustedConfidence != null && item.adjustedConfidence !== item.confidence && (
                    <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-0.5 text-[10px] text-[var(--color-primary)]">
                      {percent(item.adjustedConfidence)} adjusted
                    </span>
                  )}
                </div>
              </div>
              <p className="mt-1 text-[var(--color-muted-foreground)]">
                {item.sourceEventType} · {prettyFamily(item.signalFamily)} → {item.intent}
              </p>
              <div className="mt-2 flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => onSourceEventTypeSelect?.(item.sourceEventType, item.signalFamily)}
                  className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-0.5 text-[10px] text-[var(--color-primary)] transition-colors hover:border-[var(--color-primary)]"
                >
                  Scope loop to this source
                </button>
                <button
                  type="button"
                  onClick={() => onSignalFamilySelect?.(item.signalFamily)}
                  className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                >
                  Scope family
                </button>
              </div>
              <p className="mt-1 text-[var(--color-foreground)]">
                {item.reason}
              </p>
              {item.executionPlanReason && item.executionPlanReason !== item.reason && (
                <p className="mt-1 text-[var(--color-muted-foreground)]">
                  Execution: {item.executionPlanReason}
                </p>
              )}
              {item.observedMaterializationRate != null && item.observedOutcomeConfidence && item.observedOutcomeConfidence !== "none" && (
                <p className="mt-1 text-[var(--color-primary)]">
                  Outcome evidence: {percent(item.observedMaterializationRate)} materialized · {item.observedOutcomeConfidence} confidence
                  {item.rankScore == null ? "" : ` · rank ${item.rankScore}`}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
